import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { withStyles } from '../tools/withStyles';
import { Main } from '../components/Main';
import { withSounds } from '../tools/withSounds';
import { withLanguage } from '../tools/withLanguage';
import { Secuence } from '../components/Secuence';
import { Text } from '../components/Text';
import { Fader } from '../components/Fader';
import { AlbumPlayer } from '../components/AlbumPlayer';
import albums from '../data/albums.json';

const playableAlbums = albums.filter(album => album.webamp && album.webamp.tracks && album.webamp.tracks.length);
const normalizedTracks = playableAlbums.flatMap(album => {
  const albumCover = album.cover ? album.cover.src : null;
  const albumArtist = album.webamp.artist || album.band;
  return (album.webamp.tracks || []).map(track => {
    const rawPath = track.url || track.file;
    const trackUrl = rawPath ? encodeURI(rawPath) : null;
    const displayArtist = track.artist || albumArtist;
    return {
      ...track,
      albumTitle: album.title,
      albumCover,
      albumArtist,
      url: trackUrl,
      sourcePath: rawPath,
      defaultName: `${displayArtist || 'Unknown Artist'} - ${track.title}`,
      metaData: {
        artist: displayArtist,
        title: track.title,
        album: album.title
      }
    };
  });
}).filter(track => track.url);
const primaryAlbum = playableAlbums[0] || null;
const trackIndexMap = normalizedTracks.reduce((map, track, index) => {
  if (track.url) {
    map.set(track.url, index);
  }
  return map;
}, new Map());

const styles = () => ({
  root: {},
  playerSection: {
    paddingTop: 0,
    marginBottom: 48,
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    position: 'relative',
    zIndex: 1000
  },
  playerLayout: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 24,
    width: '100%',
    maxWidth: 1100,
    padding: 0,
    margin: '0 auto'
  },
  albumPlayerShell: {
    padding: 0,
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,0.08)',
    background: '#0b0b12',
    boxShadow: '0 30px 80px rgba(0,0,0,0.55)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    justifySelf: 'center',
    marginLeft: 0,
    position: 'relative',
    zIndex: 1000,
    opacity: 1,
    width: '100%',
    maxWidth: 1100
  },
  trackSection: {
    minWidth: 360,
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,0.06)',
    background: 'rgba(8,8,12,0.65)',
    padding: 24,
    boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
    justifySelf: 'start',
    width: '100%',
    maxWidth: 'none',
    marginTop: 16,
    position: 'relative',
    scrollMarginTop: 80
  },
  playerSubtext: {
    margin: [0, 0, 28],
    opacity: 0.75,
    textAlign: 'left'
  },
  trackGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gridAutoRows: 'auto',
    gridGap: 22,
    paddingRight: 6
  },
  trackButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    textAlign: 'left',
    width: '100%'
  },
  trackCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 20,
    borderRadius: 18,
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(8, 8, 12, 0.75)',
    color: '#fff',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
    transition: 'border-color 220ms ease, box-shadow 220ms ease, transform 220ms ease, background 220ms ease',
    '&::before': {
      content: "''",
      position: 'absolute',
      inset: 0,
      borderRadius: 18,
      boxShadow: '0 0 35px rgba(0,255,200,0.12)',
      opacity: 0,
      transition: 'opacity 220ms ease'
    },
    '&:hover': {
      borderColor: 'rgba(0,255,200,0.65)',
      boxShadow: '0 18px 45px rgba(0,255,200,0.18)',
      transform: 'translateY(-6px) scale(1.015)',
      background: 'rgba(10, 14, 24, 0.95)'
    },
    '&:hover::before': {
      opacity: 1
    },
    '&:hover $trackCoverOverlay': {
      opacity: 1,
      transform: 'translateY(0)'
    },
    '&$trackCardActive $trackCoverOverlay': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  },
  trackCardActive: {
    borderColor: '#00ffc8',
    boxShadow: '0 16px 35px rgba(0,255,200,0.18)',
    transform: 'translateY(-4px)',
    '&::before': {
      opacity: 1
    },
    '& $trackCoverOverlay': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  },
  trackCoverWrapper: {
    position: 'relative',
    width: '100%',
    marginBottom: 14
  },
  trackCover: {
    display: 'block',
    width: '100%',
    paddingBottom: '100%',
    borderRadius: 12,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundColor: '#111',
    border: '1px solid rgba(255,255,255,0.12)'
  },
  trackCoverOverlay: {
    position: 'absolute',
    inset: 0,
    borderRadius: 12,
    border: '1px solid rgba(0,255,200,0.35)',
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 180ms ease, transform 220ms ease',
    transform: 'translateY(18px)',
    background: 'linear-gradient(120deg, rgba(0,255,200,0.08), rgba(0,255,200,0.03))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&::before': {
      content: "''",
      position: 'absolute',
      inset: 10,
      borderRadius: 10,
      border: '1px dashed rgba(0,255,200,0.35)',
      opacity: 0.65
    }
  },
  trackOverlayIcon: {
    fontSize: 22,
    letterSpacing: 3,
    color: '#00ffc8',
    fontFamily: 'Orbitron, sans-serif',
    textTransform: 'uppercase'
  },
  trackInfo: {
    flexGrow: 1,
    textAlign: 'center'
  },
  trackTitle: {
    display: 'block',
    fontSize: 15,
    fontWeight: 600,
    margin: [0, 0, 4]
  },
  trackArtist: {
    display: 'block',
    fontSize: 13,
    opacity: 0.75
  },
  albumPlayer: {
    width: '100%',
    textAlign: 'center',
    '& #webamp': {
      position: 'relative !important',
      top: 'auto !important',
      left: 'auto !important',
      margin: '0 auto !important',
      marginTop: '0 !important',
      display: 'inline-block',
      zIndex: 2000,
      background: 'rgba(0,0,0,0.6)',
      pointerEvents: 'auto'
    }
  },
  '@media screen and (max-width: 1024px)': {
    trackGrid: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
    }
  }
});

class Music extends React.Component {
  static propTypes = {
    classes: PropTypes.object,
    sounds: PropTypes.object,
    t: PropTypes.func
  };

  state = {
    currentTrackUrl: null
  };

  trackSectionRef = React.createRef();
  scrollTimeouts = [];

  scrollToTop = () => {
    try {
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        window.scrollTo(0, 0);
      }
    } catch (e) {}
  };

  componentDidMount () {
    try {
      if (typeof window !== 'undefined' && window.history && 'scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
    } catch (e) {}

    const resets = [0, 120, 350, 700];
    resets.forEach(delay => {
      const id = setTimeout(() => this.scrollToTop(), delay);
      this.scrollTimeouts.push(id);
    });
  }

  getCoverStyle (coverImage) {
    if (!coverImage) {
      return undefined;
    }
    const encoded = encodeURI(coverImage);
    return { backgroundImage: `url("${encoded}")` };
  }

  componentWillUnmount () {
    this.scrollTimeouts.forEach(id => clearTimeout(id));
    this.scrollTimeouts = [];
    if (this.trackChangeUnsubscribe) {
      this.trackChangeUnsubscribe();
      this.trackChangeUnsubscribe = null;
    }
  }

  getTranslatedTracks () {
    const { t } = this.props;
    const unknownArtist = t ? t('music.unknownArtist') : 'Unknown Artist';

    return normalizedTracks.map(track => {
      const artist = track.artist || track.albumArtist || unknownArtist;
      const metaData = {
        ...(track.metaData || {}),
        artist: (track.metaData && track.metaData.artist) || artist,
        title: (track.metaData && track.metaData.title) || track.title,
        album: (track.metaData && track.metaData.album) || track.albumTitle
      };

      return {
        ...track,
        artist,
        displayArtist: artist,
        defaultName: track.defaultName
          ? track.defaultName.replace('Unknown Artist', unknownArtist)
          : `${artist} - ${track.title}`,
        metaData
      };
    });
  }

  scrollToPlayer () {
    if (this.trackSectionRef && this.trackSectionRef.current && typeof this.trackSectionRef.current.scrollIntoView === 'function') {
      try {
        this.trackSectionRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
      } catch (e) {
        this.trackSectionRef.current.scrollIntoView();
      }
    }
  }

  handleWebampReady = webamp => {
    if (this.trackChangeUnsubscribe) {
      this.trackChangeUnsubscribe();
      this.trackChangeUnsubscribe = null;
    }

    this.webamp = webamp;
    this.scrollToTop();
    this.scrollToPlayer();

    if (!webamp) {
      this.setState({ currentTrackUrl: null });
      return;
    }

    this.trackChangeUnsubscribe = webamp.onTrackDidChange(trackInfo => {
      this.setState({ currentTrackUrl: trackInfo ? trackInfo.url : null });
    });
  };

  handleTrackSelect = track => {
    const { sounds } = this.props;
    if (sounds && sounds.click) {
      sounds.click.play();
    }

    if (!track || !this.webamp) {
      return;
    }

    const translatedTracks = this.getTranslatedTracks();

    if (!translatedTracks.length || !track.url) {
      return;
    }

    const trackIndex = trackIndexMap.get(track.url);
    if (trackIndex == null || !this.webamp) {
      return;
    }

    // Toggle if clicking the currently selected track
    if (this.state.currentTrackUrl === track.url) {
      try {
        const status = typeof this.webamp.getMediaStatus === 'function'
          ? this.webamp.getMediaStatus()
          : 'PLAYING';
        if (status === 'PLAYING') {
          if (typeof this.webamp.pause === 'function') this.webamp.pause();
        } else {
          if (typeof this.webamp.play === 'function') this.webamp.play();
        }
      } catch (err) {
        console.error('[Music] Toggle play/pause failed', err);
      }
      return;
    }

    const rotatedTracks = translatedTracks
      .slice(trackIndex)
      .concat(translatedTracks.slice(0, trackIndex));

    try {
      this.webamp.setTracksToPlay(rotatedTracks);
    } catch (error) {
      console.error('[Music] Unable to start playback', error);
    }
  };

  render () {
    const { classes, sounds, t } = this.props;
    const { currentTrackUrl } = this.state;
    const translatedTracks = this.getTranslatedTracks();
    const hasTracks = translatedTracks.length > 0;
    const unknownArtist = t ? t('music.unknownArtist') : 'Unknown Artist';

    return (
      <Main className={classes.root} flushLeft style={{ opacity: 1 }}>
        {/* Dev widget: inject via Helmet when allowed; dynamic insertion is a fallback */}
        {/* Widget now injected globally via Layout */}
        <Secuence stagger>
          {!hasTracks && (
            <p><Text>{t ? t('music.missing') : 'Audio files not found. Add tracks under static/music to populate this page.'}</Text></p>
          )}
          {hasTracks && (
            <section className={classes.playerSection} style={{ position: 'relative', zIndex: 20 }}>
              <div className={classes.playerLayout}>
                <div
                  className={classes.albumPlayerShell}
                  data-testid='webamp-shell'
                >
                  <AlbumPlayer
                    className={classes.albumPlayer}
                    artist={primaryAlbum ? (primaryAlbum.webamp.artist || primaryAlbum.band) : unknownArtist}
                    tracks={translatedTracks}
                    onReady={this.handleWebampReady}
                  />
                </div>
                <div
                  className={classes.trackSection}
                  ref={this.trackSectionRef}
                  data-testid='track-section'
                >
                  <p className={classes.playerSubtext}><Text>{t ? t('music.selectTrack') : 'Select any single to launch playback.'}</Text></p>
                  <div className={classes.trackGrid}>
                    {translatedTracks.map(track => {
                      const coverImage = track.cover || track.albumCover || (primaryAlbum && primaryAlbum.cover ? primaryAlbum.cover.src : null) || '/images/soulextract.jpg';
                      const isActive = currentTrackUrl === track.url;
                      return (
                        <button
                          key={track.url}
                          type='button'
                          className={cx(classes.trackButton, classes.trackCard, isActive && classes.trackCardActive)}
                          onClick={() => this.handleTrackSelect(track)}
                          onMouseEnter={() => sounds && sounds.hover && sounds.hover.play()}
                        >
                          <div className={classes.trackCoverWrapper}>
                            <span
                              className={classes.trackCover}
                              style={this.getCoverStyle(coverImage)}
                            />
                            <span className={classes.trackCoverOverlay}>
                              <span className={classes.trackOverlayIcon}>
                                {isActive ? (t ? t('music.pause') : '⏸ PAUSE') : (t ? t('music.play') : '▶ PLAY')}
                              </span>
                            </span>
                          </div>
                          <span className={classes.trackInfo}>
                            <span className={classes.trackTitle}>{track.title}</span>
                            <span className={classes.trackArtist}>
                              {track.displayArtist || track.artist || track.albumArtist || unknownArtist}
                              {track.albumTitle ? ` • ${track.albumTitle}` : ''}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>
          )}
        </Secuence>
      </Main>
    );
  }
}

export default withStyles(styles)(withSounds()(withLanguage()(Music)));
